import {
  Component, OnInit, OnDestroy, inject, signal, computed, DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  Firestore, doc,
  collection, addDoc, deleteDoc, serverTimestamp, writeBatch
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subscription } from 'rxjs';
import { Asset, ScheduleException } from '../../models/asset.model';
import {
  Booking, BookingStatus, BookingStatusMeta, BOOKING_STATUS_META,
  isCalendarBlockingStatus,
} from '../../models/booking.model';
import { AssetService } from '../../services/asset.service';
import { SpacePublicRouteService } from '../../services/space-public-route.service';
import { NavigationService, OWNER_ROUTES } from '../../services/navigation.service';
import { BookingService } from '../../services/booking.service';
import { MONTH_NAMES, SLOT_ROW_HEIGHT } from '../../shared/schedule/schedule.constants';
import {
  dayOfWeekMon0, normalizeDateValue, normalizeStatus, parseCloseHour, parseHour, slotTime, toDateStr,
  weekDatesFromMonday,
} from '../../shared/schedule/schedule.utils';
import { dayHoursForDate, dayOperatingWindowForDate } from '../../shared/schedule/schedule-pricing.utils';
import { buildVisibleSlots, findSlotIndex, GridSlot, slotFractions } from '../../shared/schedule/slot-grid.utils';
import {
  applyBlockingBookingsToDay,
  applyFixedBlocksToDay,
  applySeasonalBlocksToDay,
  collectOccupiedSlotIndices,
  isSlotReleasedByException,
  OccupancyCellMeta,
  seasonalBlocksForDay,
} from '../../shared/schedule/schedule-occupancy.utils';
import { logicalScheduleDay } from '../../shared/schedule/schedule-pricing.utils';

// ── Pure helpers ─────────────────────────────────────────────────────────────
interface EventStyle {
  bgClass:     string;
  textClass:   string;
  borderColor: string;
  iconName:    string;
}
function bookingStyle(booking: Booking | null, isFixed: boolean): EventStyle {
  if (isFixed) {
    return { bgClass:'bg-slate-800', textClass:'text-white', borderColor:'#334155', iconName:'repeat' };
  }
  switch (booking?.status) {
    case 'MANUAL_BLOCK':     return { bgClass:'bg-gray-400', textClass:'text-white', borderColor:'#6b7280', iconName:'ban' };
    case 'PENDING_FORMALIZATION': return { bgClass:'bg-amber-50',  textClass:'text-amber-700', borderColor:'#f59e0b', iconName:'file-text' };
    case 'PENDING_VERIFICATION': return { bgClass:'bg-slate-100', textClass:'text-slate-700', borderColor:'#94a3b8', iconName:'eye' };
    case 'CONFIRMED':        return { bgClass:'bg-slate-900', textClass:'text-white', borderColor:'#0B3558', iconName:'circle-check' };
    case 'ACTIVE':           return { bgClass:'bg-slate-900', textClass:'text-white', borderColor:'#0B3558', iconName:'activity' };
    case 'COMPLETED':        return { bgClass:'bg-gray-100',  textClass:'text-gray-400',  borderColor:'#9ca3af', iconName:'archive' };
    default:                 return { bgClass:'bg-gray-100',  textClass:'text-gray-400',  borderColor:'#d1d5db', iconName:'circle' };
  }
}

function occupancyBlockStyle(status: string, booking: Booking | null): EventStyle {
  switch (status) {
    case 'FIXED':    return bookingStyle(null, true);
    case 'SEASONAL': return { bgClass:'bg-orange-50', textClass:'text-orange-800', borderColor:'#ea580c', iconName:'calendar-range' };
    default:         return bookingStyle(booking, false);
  }
}

// ── Data interfaces ───────────────────────────────────────────────────────────
export type DotColor = 'green' | 'gray' | 'red' | 'none';
interface CalDay   { date: Date; dateStr: string; inMonth: boolean; dot: DotColor; }
interface CalMonth { year: number; month: number; label: string; weeks: CalDay[][]; }
interface BlockForm { date: string; startTime: string; endTime: string; label: string; }

interface GridEvent {
  startSlotIdx: number;
  spanSlots:    number;
  booking:      Booking | null;
  isFixed:      boolean;
  /** FIXED | SEASONAL | booking status — drives release vs delete. */
  blockStatus:  string;
  title:        string;
  timeRange:    string;
  meta:         BookingStatusMeta | null;
  bgClass:      string;
  textClass:    string;
  borderColor:  string;
  iconName:     string;
  /** Start/end time strings — needed to release a recurring block on one date. */
  startTime?:   string;
  endTime?:     string;
}
interface GridColumn {
  date:     Date;
  events:   GridEvent[];
  freeMask: boolean[];
  /** Past or out-of-range slots — diagonal stripes, no block action. */
  inactiveMask: boolean[];
  /** Cells whose recurring fixed-block was released for this date (open but not booked). */
  releasedMask: boolean[];
}
interface WeekGridData {
  slots:   Array<{ hour: number; half: 0|1 }>;
  columns: GridColumn[];
}

// ── Component ─────────────────────────────────────────────────────────────────
@Component({
  selector: 'app-space-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './space-calendar.component.html',
})
export class SpaceCalendarComponent implements OnInit, OnDestroy {
  private route        = inject(ActivatedRoute);
  private router       = inject(Router);
  private firestore    = inject(Firestore);
  private auth         = inject(Auth);
  private assetService = inject(AssetService);
  private bookingService = inject(BookingService);
  private spaceRoutes = inject(SpacePublicRouteService);
  private navService = inject(NavigationService);
  private destroyRef = inject(DestroyRef);

  /** Height in px of one 30-minute slot row (aligned with schedule-step). */
  readonly ROW_HEIGHT = SLOT_ROW_HEIGHT;

  readonly DAYS_SHORT  = ['Lun','Mar','Mie','Jue','Vie','Sab','Dom'];
  readonly MONTH_NAMES = MONTH_NAMES;

  readonly spaceId        = signal<string>('');
  readonly space          = signal<Asset|null>(null);
  readonly loading        = signal(true);
  readonly bookings       = signal<Booking[]>([]);
  readonly exceptions     = signal<ScheduleException[]>([]);
  readonly weekStart      = signal<Date>(this.#mondayOf(this.#todayDate()));
  readonly selectedDate   = signal<Date>(this.#todayDate());
  readonly miniMonthStart = signal(0);
  readonly blockModalOpen = signal(false);
  readonly blockForm      = signal<BlockForm>({ date:'', startTime:'', endTime:'', label:'' });
  readonly blockSaving    = signal(false);
  /** Owners may view / block-create from today through one year ahead. */
  readonly blockAllowedDate = this.#todayDateStr();
  readonly calendarViewMaxDate = this.#maxViewDateStr();

  /** Release-modal state — open when an owner clicks a recurrent fixed-block instance. */
  readonly releaseTarget  = signal<{
    date: string; startTime: string; endTime: string; label: string;
  } | null>(null);
  readonly releaseSaving  = signal(false);

  /** Delete-block modal state — open when an owner clicks a MANUAL_BLOCK booking. */
  readonly deleteTarget   = signal<Booking | null>(null);
  readonly deleteSaving   = signal(false);

  readonly timeOptions = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? '00' : '30';
    return `${String(h).padStart(2, '0')}:${m}`;
  });

  readonly blockTimeOptions = computed<string[]>(() => {
    const space = this.space();
    if (!space) return this.timeOptions;
    const open  = parseHour(space.openingTime ?? '08:00');
    const close = parseHour(space.closingTime ?? '22:00');
    return this.timeOptions.filter(t => {
      const v = parseHour(t);
      return v >= open && v <= close;
    });
  });

  private subs: Subscription[] = [];

  // ── Mini-calendar (single month + prev/next) ─────────────────────────────────
  readonly miniCalendar = computed<CalMonth>(() => {
    const today = new Date();
    return this.#buildMonth(today, this.miniMonthStart());
  });

  readonly miniMonthOffsetMin = computed(() =>
    this.#monthOffsetFromToday(this.#mondayOf(this.#todayDate())),
  );

  readonly miniMonthOffsetMax = computed(() => {
    const max = this.#parseDateStr(this.calendarViewMaxDate);
    const today = new Date();
    return (
      (max.getFullYear() - today.getFullYear()) * 12 +
      (max.getMonth() - today.getMonth())
    );
  });

  readonly weekDates = computed(() => weekDatesFromMonday(this.weekStart()));

  // ── Main grid (merged event blocks) ─────────────────────────────────────────
  readonly weekGridData = computed<WeekGridData>(() => {
    const space = this.space();
    if (!space) return { slots: [], columns: [] };

    const weekDates    = this.weekDates();
    const weekDateStrs = weekDates.map(toDateStr);
    const weekBookings = this.bookings().filter(b => {
      const d = normalizeDateValue(b.date);
      const s = normalizeStatus(b.status);
      return weekDateStrs.includes(d) && isCalendarBlockingStatus(s);
    });

    const slots = buildVisibleSlots(space.openingTime, space.closingTime);
    const slotCount = slots.length;
    const fracs = slotFractions(slots);
    const exceptions = this.exceptions();

    const columns: GridColumn[] = weekDates.map((date, ci) => {
      const dateStr      = weekDateStrs[ci];
      const { open: openStr, close: closeStr } = dayHoursForDate(space, date, dateStr);
      const open  = parseHour(openStr);
      const close = parseCloseHour(closeStr, openStr);
      const freeMask     = new Array<boolean>(slotCount).fill(true);
      const releasedMask = new Array<boolean>(slotCount).fill(false);
      const busyMeta: (OccupancyCellMeta | null)[] = new Array(slotCount).fill(null);

      applyFixedBlocksToDay({
        space, date, dateStr, slots, slotFractions: fracs,
        open, close, exceptions, freeMask, busyMeta,
      });
      applySeasonalBlocksToDay({
        space, date, dateStr, slots, slotFractions: fracs,
        open, close, exceptions, freeMask, busyMeta,
      });
      applyBlockingBookingsToDay({
        bookings: weekBookings,
        dateStr,
        slots,
        open,
        close,
        freeMask,
        busyMeta,
      });

      const logicalDay = logicalScheduleDay(date, dateStr, space);
      for (const fb of space.fixedBlocks ?? []) {
        if (fb.day !== logicalDay) continue;
        const idx = slots.findIndex(s => s.hour === fb.hour && s.half === fb.half);
        if (idx === -1) continue;
        if (isSlotReleasedByException(dateStr, fracs[idx], exceptions)) {
          releasedMask[idx] = true;
        }
      }
      for (const sb of seasonalBlocksForDay(space, date, dateStr)) {
        const startIdx = Math.max(0, findSlotIndex(slots, parseHour(sb.startTime)));
        let endIdx = findSlotIndex(slots, parseHour(sb.endTime));
        if (endIdx === -1) endIdx = slotCount;
        for (let idx = startIdx; idx < endIdx; idx++) {
          if (isSlotReleasedByException(dateStr, fracs[idx], exceptions)) {
            releasedMask[idx] = true;
          }
        }
      }

      const events = this.#gridEventsFromOccupancy({
        dateStr,
        slots,
        busyMeta,
        bookings: weekBookings,
        space,
      });

      const inactiveMask = new Array<boolean>(slotCount).fill(false);
      const inRange = this.isDateInViewRange(date);
      for (let si = 0; si < slotCount; si++) {
        const slot = slots[si];
        inactiveMask[si] = !inRange || this.#isPastSlot(date, slot.hour, slot.half);
      }

      return { date, events, freeMask, inactiveMask, releasedMask };
    });

    return { slots, columns };
  });

  // ── Lifecycle ────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      void this.#initFromRoute();
    });
  }

  async #initFromRoute(): Promise<void> {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    this.loading.set(true);

    const paramMap = this.route.snapshot.paramMap;
    const parsed = this.spaceRoutes.parseRoute(paramMap);
    if (!parsed) {
      this.goBack();
      return;
    }

    const space = await this.spaceRoutes.loadSpaceFromRoute(paramMap);
    if (!space) {
      this.goBack();
      return;
    }

    if (parsed.kind === 'slug') {
      this.spaceRoutes.redirectToCanonicalIfNeeded(space, 'calendario');
    }

    const id = space.id!;
    this.spaceId.set(id);

    this.subs.push(
      this.assetService.watchSpace(id).subscribe({
        next: data => { this.space.set(data); this.loading.set(false); },
        error: ()  => { this.loading.set(false); },
      }),
    );

    const start = this.blockAllowedDate;
    const end   = this.calendarViewMaxDate;
    // ownerUid filter is required by the /bookings read rule for list queries —
    // Firestore validates rule satisfiability against query constraints.
    const uid = this.auth.currentUser?.uid ?? '';
    this.subs.push(
      this.bookingService.getOwnerSpaceBookings(id, uid, { start, end })
        .subscribe(data => this.bookings.set(data)),
    );

    this.subs.push(
      this.bookingService.getExceptions(id, { start, end })
        .subscribe(data => this.exceptions.set(data)),
    );
  }

  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  // ── Navigation ────────────────────────────────────────────────────────────────
  prevWeek(): void {
    if (!this.canPrevWeek()) return;
    const d = new Date(this.weekStart()); d.setDate(d.getDate() - 7);
    this.weekStart.set(d); this.#ensureWeekVisible(d);
  }
  nextWeek(): void {
    if (!this.canNextWeek()) return;
    const d = new Date(this.weekStart()); d.setDate(d.getDate() + 7);
    this.weekStart.set(d); this.#ensureWeekVisible(d);
  }
  prevMiniMonth(): void {
    if (!this.canPrevMiniMonth()) return;
    this.miniMonthStart.update(v => v - 1);
  }
  nextMiniMonth(): void {
    if (!this.canNextMiniMonth()) return;
    this.miniMonthStart.update(v => v + 1);
  }
  canPrevMiniMonth(): boolean {
    return this.miniMonthStart() > this.miniMonthOffsetMin();
  }
  canNextMiniMonth(): boolean {
    return this.miniMonthStart() < this.miniMonthOffsetMax();
  }
  goToToday(): void {
    const today  = this.#todayDate();
    const monday = this.#mondayOf(today);
    this.miniMonthStart.set(this.#monthOffsetFromToday(monday));
    this.weekStart.set(monday);
    this.selectedDate.set(today);
  }

  goToWeekOf(date: Date): void {
    if (!this.isDateInViewRange(date)) return;
    const d = new Date(date);
    d.setDate(d.getDate() - dayOfWeekMon0(d));
    d.setHours(0, 0, 0, 0);
    this.weekStart.set(d);
    this.selectedDate.set(date);
    this.#ensureWeekVisible(d);
  }

  /** True when `date` falls within the week currently shown on the right (Mon–Sun). */
  isInDisplayedWeek(date: Date): boolean {
    const monday = this.weekStart();
    const d0 = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const m0 = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate()).getTime();
    return d0 >= m0 && d0 < m0 + 7 * 24 * 60 * 60 * 1000;
  }

  // ── Block modal ───────────────────────────────────────────────────────────────
  openBlockModal(date: Date, hour: number, half: 0|1): void {
    if (!this.isDateInViewRange(date)) return;
    if (this.#isPastSlot(date, hour, half)) return;
    const selectedDate = toDateStr(date);

    const start = slotTime(hour, half);
    const endH  = half === 1 ? hour + 1 : hour;
    const endHf = half === 1 ? 0 : 1;
    const end   = slotTime(endH, endHf as 0|1);
    this.blockForm.set({ date: selectedDate, startTime: start, endTime: end, label: '' });
    this.blockModalOpen.set(true);
  }

  closeBlockModal(): void { this.blockModalOpen.set(false); }

  /** FIXED or SEASONAL — released for one date via scheduleExceptions. */
  isRecurringBlock(event: GridEvent): boolean {
    return event.blockStatus === 'FIXED' || event.blockStatus === 'SEASONAL';
  }

  /** Future recurring block or manual block the owner can remove from the grid. */
  canRemoveCalendarEvent(event: GridEvent, date: Date): boolean {
    if (!this.isOwner() || !this.isReleasable(date)) return false;
    const slot = this.weekGridData().slots[event.startSlotIdx];
    if (!slot || this.#isPastSlot(date, slot.hour, slot.half)) return false;
    if (this.isRecurringBlock(event)) {
      return !!event.startTime && !!event.endTime;
    }
    return event.booking?.status === 'MANUAL_BLOCK';
  }

  // ── Release modal (recurring fixed / seasonal blocks → scheduleExceptions) ───
  openReleaseModal(event: GridEvent, date: Date): void {
    if (!this.isRecurringBlock(event) || !event.startTime || !event.endTime) return;
    if (!this.canRemoveCalendarEvent(event, date)) return;
    // Only the owner of the space can release. The Firestore rule is the source-of-truth;
    // this is a UI affordance to avoid showing the action to non-owners.
    const space = this.space();
    const uid   = this.auth.currentUser?.uid ?? '';
    if (!space || space.ownerId !== uid) return;

    this.releaseTarget.set({
      date:      toDateStr(date),
      startTime: event.startTime,
      endTime:   event.endTime,
      label:     event.title || '',
    });
  }

  closeReleaseModal(): void { this.releaseTarget.set(null); }

  // ── Delete manual-block modal ───────────────────────────────────────────────
  openDeleteBlockModal(event: GridEvent, date: Date): void {
    const booking = event.booking;
    if (!booking?.id || booking.status !== 'MANUAL_BLOCK') return;
    if (!this.canRemoveCalendarEvent(event, date)) return;
    this.deleteTarget.set(booking);
  }

  closeDeleteBlockModal(): void { this.deleteTarget.set(null); }

  async confirmDeleteBlock(): Promise<void> {
    if (this.deleteSaving()) return;
    const booking = this.deleteTarget();
    if (!booking?.id) return;

    this.deleteSaving.set(true);
    try {
      await deleteDoc(doc(this.firestore, 'bookings', booking.id));
      this.deleteTarget.set(null);
    } finally {
      this.deleteSaving.set(false);
    }
  }

  async saveRelease(): Promise<void> {
    if (this.releaseSaving()) return;
    const target = this.releaseTarget();
    const uid    = this.auth.currentUser?.uid ?? '';
    if (!target || !uid || !this.spaceId()) return;

    this.releaseSaving.set(true);
    try {
      // One doc per run (whole event released on this date). Batched in case
      // we ever extend this to multi-run releases — keeps the write atomic.
      const batch = writeBatch(this.firestore);
      const ref   = doc(collection(this.firestore, `assets/${this.spaceId()}/scheduleExceptions`));
      batch.set(ref, {
        date:          target.date,
        startTime:     target.startTime,
        endTime:       target.endTime,
        originalLabel: target.label,
        releasedBy:    uid,
        releasedAt:    serverTimestamp(),
      });
      await batch.commit();
      this.releaseTarget.set(null);
    } finally {
      this.releaseSaving.set(false);
    }
  }

  async saveBlock(): Promise<void> {
    if (this.blockSaving()) return;
    const form = this.blockForm();
    const uid  = this.auth.currentUser?.uid ?? '';
    if (!form.date || !form.startTime || !form.endTime || !this.spaceId()) return;
    if (!this.isDateInViewRange(this.#parseDateStr(form.date))) return;
    const startSlot = this.#slotFromTime(form.startTime);
    if (this.#isPastSlot(this.#parseDateStr(form.date), startSlot.hour, startSlot.half)) return;
    if (parseHour(form.endTime) <= parseHour(form.startTime)) return;
    this.blockSaving.set(true);
    try {
      await addDoc(collection(this.firestore, 'bookings'), {
        spaceId:   this.spaceId(), ownerUid: uid, userId: '',
        date:      form.date, startTime: form.startTime, endTime: form.endTime,
        status:    'MANUAL_BLOCK', label: form.label || 'Bloqueado',
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      this.blockModalOpen.set(false);
    } finally { this.blockSaving.set(false); }
  }

  updateBlockForm(patch: Partial<BlockForm>): void {
    this.blockForm.set({ ...this.blockForm(), ...patch });
  }

  // ── Utilities ─────────────────────────────────────────────────────────────────
  isToday(date: Date): boolean {
    const t = new Date();
    return date.getFullYear() === t.getFullYear() && date.getMonth() === t.getMonth() && date.getDate() === t.getDate();
  }
  isSelected(date: Date): boolean {
    const s = this.selectedDate();
    return date.getFullYear() === s.getFullYear() && date.getMonth() === s.getMonth() && date.getDate() === s.getDate();
  }
  isBlockDateAllowed(date: Date): boolean {
    return this.isDateInViewRange(date);
  }

  /** View-only window: today through one year ahead (create/delete blocks use same range). */
  isDateInViewRange(date: Date): boolean {
    const s = toDateStr(date);
    return s >= this.blockAllowedDate && s <= this.calendarViewMaxDate;
  }

  canPrevWeek(): boolean {
    const prev = new Date(this.weekStart());
    prev.setDate(prev.getDate() - 7);
    const minMonday = this.#mondayOf(this.#todayDate());
    return toDateStr(prev) >= toDateStr(minMonday);
  }

  canNextWeek(): boolean {
    const next = new Date(this.weekStart());
    next.setDate(next.getDate() + 7);
    const maxMonday = this.#mondayOf(this.#parseDateStr(this.calendarViewMaxDate));
    return toDateStr(next) <= toDateStr(maxMonday);
  }
  /** True when the current auth user owns this space (controls owner-only affordances). */
  isOwner(): boolean {
    const space = this.space();
    const uid   = this.auth.currentUser?.uid ?? '';
    return !!space && space.ownerId === uid;
  }
  /** True when the recurrent fixed-block run is in the view window (releasable). */
  isReleasable(date: Date): boolean {
    return this.isDateInViewRange(date);
  }

  /** True when week grid + mini-calendar month + selection all point at today. */
  readonly isOnDefaultView = computed(() => {
    const today  = this.#todayDate();
    const monday = this.#mondayOf(today);
    return (
      toDateStr(this.weekStart()) === toDateStr(monday) &&
      this.miniMonthStart() === this.#monthOffsetFromToday(monday) &&
      toDateStr(this.selectedDate()) === toDateStr(today)
    );
  });
  formatWeekRange(): string {
    const dates = this.weekDates();
    const fmt = (d: Date) => `${d.getDate()} ${this.MONTH_NAMES[d.getMonth()].slice(0, 3)}`;
    return `${fmt(dates[0])} - ${fmt(dates[6])} ${dates[6].getFullYear()}`;
  }
  formatHour(h: number, half: 0|1): string { return slotTime(h, half); }
  trackByDay(_: number, d: CalDay): string    { return d.dateStr; }
  trackByWeek(_: number, w: CalDay[]): string { return w[0].dateStr; }
  goBack(): void {
    this.navService.setView('mis-espacios');
    void this.router.navigate(OWNER_ROUTES.spaces);
  }

  #todayDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  #mondayOf(d: Date): Date {
    const m = new Date(d);
    m.setDate(m.getDate() - dayOfWeekMon0(m));
    m.setHours(0, 0, 0, 0);
    return m;
  }

  #parseDateStr(dateStr: string): Date {
    const [y, mo, day] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, day);
  }

  /** True when the 30-min slot has fully elapsed (today) or the day is before today. */
  #isPastSlot(date: Date, hour: number, half: 0 | 1): boolean {
    const dateStr = toDateStr(date);
    const todayStr = this.#todayDateStr();
    if (dateStr < todayStr) return true;
    if (dateStr > todayStr) return false;
    const slotEndMin = hour * 60 + half * 30 + 30;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return slotEndMin <= nowMin;
  }

  #slotFromTime(time: string): { hour: number; half: 0 | 1 } {
    const [h, m] = time.split(':').map(Number);
    return { hour: h ?? 0, half: (m ?? 0) >= 30 ? 1 : 0 };
  }

  #maxViewDateStr(): string {
    const d = this.#todayDate();
    d.setFullYear(d.getFullYear() + 1);
    return toDateStr(d);
  }

  #monthOffsetFromToday(monday: Date): number {
    const today = new Date();
    return (
      (monday.getFullYear() - today.getFullYear()) * 12 +
      (monday.getMonth() - today.getMonth())
    );
  }

  /** Shift the mini-calendar window so the given week's month is visible. */
  #ensureWeekVisible(monday: Date): void {
    const today = new Date();
    const monthsFromToday =
      (monday.getFullYear() - today.getFullYear()) * 12 +
      (monday.getMonth() - today.getMonth());
    const start = this.miniMonthStart();
    if (monthsFromToday !== start) {
      this.miniMonthStart.set(monthsFromToday);
    }
  }

  #todayDateStr(): string {
    return toDateStr(this.#todayDate());
  }

  /** Merges consecutive occupied cells into grid events (owner view — full labels). */
  #gridEventsFromOccupancy(params: {
    dateStr: string;
    slots: GridSlot[];
    busyMeta: (OccupancyCellMeta | null)[];
    bookings: Booking[];
    space: Asset;
  }): GridEvent[] {
    const { dateStr, slots, busyMeta, bookings, space } = params;
    const events: GridEvent[] = [];
    let i = 0;

    while (i < busyMeta.length) {
      if (!busyMeta[i]) { i++; continue; }

      const meta = busyMeta[i]!;
      let j = i + 1;
      while (
        j < busyMeta.length
        && busyMeta[j]
        && busyMeta[j]!.status === meta.status
        && busyMeta[j]!.label === meta.label
      ) {
        j++;
      }

      const startSlot = slots[i];
      const endSlot = j < slots.length ? slots[j] : null;
      const startStr = slotTime(startSlot.hour, startSlot.half);
      const endStr = endSlot
        ? slotTime(endSlot.hour, endSlot.half)
        : (space.closingTime ?? '');

      const booking = this.#findBookingForRange(dateStr, meta.status, startStr, endStr, bookings);
      const isFixed = meta.status === 'FIXED';
      const isRecurring = meta.status === 'FIXED' || meta.status === 'SEASONAL';
      const isBlock = meta.status === 'MANUAL_BLOCK';
      const style = occupancyBlockStyle(meta.status, booking);
      const bookingStatus = booking
        ? (normalizeStatus(booking.status) as BookingStatus)
        : null;

      let title = meta.label;
      if (!title) {
        if (meta.status === 'FIXED') title = 'No disponible';
        else if (meta.status === 'SEASONAL') title = 'Bloqueo temporal';
        else if (isBlock) title = 'Bloqueo Manual';
        else if (bookingStatus) title = BOOKING_STATUS_META[bookingStatus]?.label ?? meta.status;
        else title = meta.status;
      }
      if (meta.cancelableOnFullDay && isRecurring) {
        title = `${title} · cancelable en día completo`;
      }

      events.push({
        startSlotIdx: i,
        spanSlots: j - i,
        booking,
        isFixed,
        blockStatus: meta.status,
        title,
        timeRange: `${startStr} – ${endStr}`,
        meta: bookingStatus ? (BOOKING_STATUS_META[bookingStatus] ?? null) : null,
        startTime: isRecurring ? startStr : undefined,
        endTime: isRecurring ? endStr : undefined,
        ...style,
      });

      i = j;
    }

    return events;
  }

  #findBookingForRange(
    dateStr: string,
    status: string,
    startStr: string,
    endStr: string,
    bookings: Booking[],
  ): Booking | null {
    if (status === 'FIXED' || status === 'SEASONAL') return null;
    return bookings.find(b => {
      const d = normalizeDateValue(b.date);
      const s = normalizeStatus(b.status);
      return d === dateStr && s === status && b.startTime === startStr && b.endTime === endStr;
    }) ?? null;
  }

  #buildMonth(today: Date, offset: number): CalMonth {
    const base = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const yr   = base.getFullYear();
    const mo   = base.getMonth();
    const daysInMonth = new Date(yr, mo + 1, 0).getDate();
    const firstDow    = (new Date(yr, mo, 1).getDay() + 6) % 7;
    const allBookings = this.bookings();
    const space       = this.space();
    const exceptions  = this.exceptions();
    const cells: CalDay[] = [];
    for (let i = 0; i < firstDow; i++) {
      const d = new Date(yr, mo, 1 - (firstDow - i));
      cells.push({ date: d, dateStr: toDateStr(d), inMonth: false, dot: 'none' });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const d       = new Date(yr, mo, day);
      const dateStr = toDateStr(d);
      const dayBk = allBookings.filter(b =>
        b.date === dateStr && isCalendarBlockingStatus(normalizeStatus(b.status)),
      );
      const inView  = dateStr >= this.blockAllowedDate && dateStr <= this.calendarViewMaxDate;
      let dot: DotColor = 'none';
      if (space && inView) {
        const window = dayOperatingWindowForDate(space, d, dateStr);
        if (window.closed || window.totalMin <= 0) {
          dot = 'none';
        } else {
          const openSlot  = Math.round(window.openMin / 30);
          const closeSlot = Math.round(window.closeMin / 30);
          const total     = closeSlot - openSlot;

          const occupied = collectOccupiedSlotIndices({
            space,
            date: d,
            dateStr,
            bookings: dayBk,
            exceptions,
            openSlot,
            closeSlot,
          });

          if (total <= 0)                  dot = 'none';
          else if (occupied.size === 0)    dot = 'green';
          else if (occupied.size < total)  dot = 'gray';
          else                             dot = 'red';
        }
      }
      cells.push({ date: d, dateStr, inMonth: true, dot });
    }
    const trailing = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= trailing; i++) {
      const d = new Date(yr, mo + 1, i);
      cells.push({ date: d, dateStr: toDateStr(d), inMonth: false, dot: 'none' });
    }
    const weeks: CalDay[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return { year: yr, month: mo, label: `${this.MONTH_NAMES[mo]} ${yr}`, weeks };
  }
}
